import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  ProjectContext,
  ProjectContextType,
  ProjectsMap,
  ProjectType,
} from "@/context/project";
import { getCookie, arrayToObject } from "@/lib/utils";

export const ProjectProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const [projectsMap, setProjectsMap] = React.useState<ProjectsMap>(
    {} as ProjectsMap,
  );
  const [activeProject, setActiveProject] = React.useState<ProjectType | null>(
    null,
  );
  const [recentProjects, setRecentProjectsState] = React.useState<ProjectType[]>(
    [],
  );
  const [isDigitalOceanConnected, setIsDigitalOceanConnected] = React.useState(false);

  const checkDigitalOceanConnection = React.useCallback(async () => {
    if (!activeProject?.id) return;
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_API_ENDPOINT}/auth/digitalocean/validate?projectId=${activeProject.id}`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const result = await response.json();
        setIsDigitalOceanConnected(result.data.hasToken);
      }
    } catch (error) {
      console.error("Failed to check DigitalOcean connection", error);
    }
  }, [activeProject?.id]);


  const addRecentProject = React.useCallback((project: ProjectType) => {
    setRecentProjectsState((prev) => {
      const filtered = prev.filter((p) => p.id !== project.id);
      const updated = [project, ...filtered].slice(0, 2);
      localStorage.setItem("recentProjects", JSON.stringify(updated.map((p) => p.id)));
      return updated;
    });
  }, []);

  const fetchProjects = React.useCallback(async () => {
    try {
      const res = await fetch(
        import.meta.env.VITE_PUBLIC_API_ENDPOINT + "/project",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: getCookie("session") || "",
          },
          credentials: "include",
        },
      );
      if (res.ok) {
        const resJson = await res.json();
        setProjectsMap(arrayToObject(resJson?.data));
        
        const recentProjectsData = localStorage.getItem("recentProjects");
        if (recentProjectsData && resJson?.data) {
          try {
            const recentProjectIds: string[] = JSON.parse(recentProjectsData);
            const foundRecents = recentProjectIds
              .map((id) => resJson.data.find((p: ProjectType) => p.id === id))
              .filter(Boolean) as ProjectType[];
            setRecentProjectsState(foundRecents);
          } catch (e) {
            console.error("Failed to parse recentProjects", e);
          }
        }

        // if (resJson?.data.length) {
        //   // If no active project or if the current active project is not in the new list, set the first one
        //   // eslint-disable-next-line @typescript-eslint/no-explicit-any
        //   if (!activeProject || !resJson.data.find((p: any) => p.id === activeProject.id)) {
        //     setActiveProject(resJson?.data?.[0]);
        //   }
        // }
      } else if (res.status === 401) {
        navigate("/login");
      }
    } catch (error) {
      console.log(error);
      setProjectsMap({});
      setActiveProject(null);
    }
  }, [navigate]);

  React.useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  React.useEffect(() => {
    if (activeProject?.id) {
      checkDigitalOceanConnection();
    } else {
      setIsDigitalOceanConnected(false);
    }
  }, [activeProject?.id, checkDigitalOceanConnection]);

  React.useEffect(() => {
    if (activeProject) {
      addRecentProject(activeProject);
    }
  }, [activeProject, addRecentProject]);

  const contextValue: ProjectContextType = {
    projectsMap,
    fetchProjects,
    activeProject,
    setActiveProject,
    setProjectsMap,
    recentProjects,
    addRecentProject,
    isDigitalOceanConnected,
    checkDigitalOceanConnection,
  };

  return (
    <ProjectContext.Provider value={contextValue}>
      {children}
    </ProjectContext.Provider>
  );
};
